/**
 * ⭐⭐ **THE ONE PLACE COLOUR IS ASSIGNED A MEANING.**
 *
 * The owner, on the data-plane page: *"this is a data page. Let's use color coding on the data
 * parts, healthy (green, …), any errors in red… make sure the colors are consistent across the full
 * site admin and anywhere there is data on a data page we are color coding properly.* **Remember:
 * Color is for data and status is also data. Versions of a service is NOT data on this page, for
 * example, unless it is out of sync.**"
 *
 * That last sentence is the whole specification, and it is sharper than it looks. Read as a rule:
 *
 * > ## ⛔ COLOUR ENCODES A VERDICT, NEVER A VALUE.
 * >
 * > A field may carry colour **iff the code can name which verdict its current value carries**.
 * > Identity and chrome — names, ids, slugs, regions, versions, timestamps, counts — carry no
 * > verdict, so they render in `text-primary` / `text-secondary` / `text-muted` and nothing else.
 * > When such a field *does* become a verdict (a version that is out of sync, a timestamp that is
 * > stale, a count that has exceeded a limit) the verdict belongs to the **derived predicate**, not
 * > to the field — so `deprecatedStillRunning`, not `version`, is what gets a tone.
 * >
 * > One exception lives in the nav rail. A count of items that wait on the operator, such as
 * > pending access requests, may use the brand accent pill (`bg-accent text-on-accent`). The accent
 * > marks a waiting action and carries no verdict tone.
 *
 * ## ⛔ WHY THIS FILE EXISTS: THE PACKAGE WAS DISAGREEING WITH ITSELF IN FOUR MEASURABLE WAYS
 *
 * Measured across `cp-admin-ui/src` on 2026-08-06, before this module:
 *
 *  1. **333 literal state-colour class occurrences across 37 files** *(the baseline when this module
 *     was written; the live count and its history are in `data-tone.test.tsx`'s ratchet — currently
 *     **330**, and every file this workstream or `metrics-viz` touched is at **zero**)*, routed
 *     through **at least twelve independently-authored tone maps** — `TONE_CLASS` (×4, in `DataPlanePane`,
 *     `TenantPane`, `TenantsPane`, `PeoplePane`), `SQUARE_TONE` (×2), `VERDICT_TONE`, `DRIFT_TONE`,
 *     `REPLICA_TONE`, `HEALTH_TONE`, `PRESENCE_TONE`, `COVERAGE_TONE`, `KIND_TONE` — plus a tail of
 *     inline ternaries. Twelve authors of one decision is twelve chances to disagree, and they did.
 *  2. **The same semantic at four different alphas.** "positive" is
 *     `border-state-success/40 bg-state-success/10` in three files and `/50 /10` in a fourth;
 *     the same tone is `/70` as a square fill in one pane and `/80` in another. `site-admin`
 *     reaches for `border-state-critical/30` **75 times** where this package uses `/50`, `/40` and
 *     `/30` in the same tree.
 *  3. ⛔ **One absence kind, two colour treatments.** `CellAbsence` renders every `data-absence`
 *     neutral; `InFlightPane` renders a `data-absence="notcollected"` in `text-state-warning`. Same
 *     machine-checkable kind, opposite visual claim. See {@link DATA_VERDICT_FOR_ABSENCE}.
 *  4. ✅ **The good news, also measured: ZERO raw Tailwind palette classes** (`text-red-500` and
 *     friends) in either tree. The token layer is already universally adopted — so this is not a
 *     new system, it is the **mapping layer** the token layer never got.
 *
 * ⇒ This module owns the mapping. `data-tone.test.tsx` ratchets literal state-colour classes
 * downward so the twelve maps converge here instead of becoming thirteen.
 *
 * ## ⛔ COLOUR IS NEVER THE SOLE CARRIER (and `data-verdict-tone` is why that is checkable)
 *
 * WCAG 1.4.1. Every rendering below pairs the colour with **the word** (`healthy`, `degraded`, …)
 * and with a **glyph**, and stamps `data-verdict-tone` into the DOM. The word is the primary carrier, the
 * glyph is redundancy for scanning, and colour is third — so a monochrome screenshot, a
 * colour-blind operator and a screen reader all get the same answer. `data-verdict-tone` is the half a
 * jsdom test can see: **a colour class in `className` is invisible to every assertion we can
 * write**, exactly as `data-absence` exists because prose assertions rot.
 *
 * ⛔ **THE ATTRIBUTE IS `data-verdict-tone`, AND THE NAME IS NOT A STYLE CHOICE — BOTH OBVIOUS NAMES
 * WERE ALREADY TAKEN, BY DIFFERENT VOCABULARIES.** Measured before naming it:
 *
 * · `data-verdict` — a **domain** verdict, `responsive` | `stale` | `unresponsive`
 *   (`DataPlanesPane:233`) and `deliveryVerdict(d)` (`AppRolloutPane:766`), with 4 test assertions on
 *   the literal values.
 * · `data-tone` — a **domain** badge tone, `dpStatusBadgeTone(status)` / `action.severity` /
 *   `orgNote.tone`, across 4 more call sites.
 *
 * ⇒ a third vocabulary on either name would make the attribute mean two things depending on which
 * pane emitted it, and any gate asserting *"every coloured element carries a valid tone"* would red
 * on the word `responsive`. ⚠️ And they SHOULD stay separate even where they do not collide: many
 * domain verdicts map onto five tones, so the domain word and the tone are different facts and a
 * screen must be able to assert each without the other. Reserving the third name is the cheap half
 * of keeping them apart.
 *
 * ## ⚠️ AND THE CONTRAST WAS NOT ACTUALLY FIXED — `state-success` still failed AA in light mode
 *
 * The 2026-08-05 recalibration pass in `index.css` fixed `--color-muted` and
 * `--color-state-warning` and **missed `--color-state-success`**. Re-measured here, independently,
 * against the three light backgrounds (`surface` / `surface-elevated` / `surface-muted`):
 *
 * ```
 *   #16a34a (as shipped)   3.15 : 3.30 : 3.07   ⛔ all three BELOW the 4.5:1 AA floor
 *   #12833c (this change)  4.63 : 4.84 : 4.51   ✅ hue 142.13° → 142.30°, saturation .762 → .758
 * ```
 *
 * ⛔ That mattered *because of this work, not despite it*: "healthy = green" is the flagship
 * mapping the owner asked for, so applying it across the surface without the token fix would have
 * multiplied an AA failure by every data page. Dark mode is untouched — `#5aff82` measures
 * 14.32 : 11.89 : 10.97 there. Full disposition at the token in `index.css`.
 *
 * ## ⛔⛔ §5 — AND A RAW-TOKEN CONTRAST CHECK MEASURES A COLOUR NOBODY SEES
 *
 * `primitives`' observation, and it found a real defect in this very file:
 *
 * > *"A `/60` or `/40` suffix changes the effective contrast by more than most token edits do, so a
 * > gate reading raw hex is measuring a colour that never appears on screen."*
 *
 * ⇒ the `badge` role was `bg-state-X/10` **with the label in `text-state-X`**, so the label sat on a
 * 10% tint OF ITSELF, not on the surface my gate had been checking. Composited (2026-08-06):
 *
 * ```
 *   text-state-success  on its own /10 tint   3.95  ⛔     (plain surface: 4.51 ✅)
 *   text-state-warning                        3.99  ⛔     (plain: 4.51)
 *   text-state-critical                       3.87  ⛔     (plain: 4.51)  ← worst
 *   text-state-info                           4.20  ⛔     (plain: 4.82)
 *   …and `critical` fails in DARK too: 4.17  ⛔
 * ```
 *
 * ⛔ **No alpha fixes it.** Tried every value down to `/3`: critical only reaches 4.32. The tokens sit
 * at ~4.5 on a plain surface, so they have **zero headroom to survive a tint of themselves** — the
 * lever is not the alpha.
 *
 * ⭐ **THE FIX SPLITS THE TWO WCAG FLOORS BETWEEN THE TWO CHANNELS**, which is what the standard
 * actually asks for and what mature design systems do (GitHub Primer's `Label` uses a distinct
 * `fgColor` from its `bgColor-muted`, for exactly this reason):
 *
 * | channel | is it text? | floor | measured worst |
 * |---|---|---|---|
 * | the **label** → `text-primary` | yes | 1.4.3, **4.5:1** | **9.96:1** |
 * | the **glyph** → the state colour | no — a graphical object | 1.4.11, **3:1** | **3.87:1** |
 * | the **fill + border** → tinted state colour | no | 1.4.11 | (decorative; see below) |
 *
 * ⚠️ **The verdict is still carried three ways** — fill, border and glyph — plus the word. So this is
 * *more* 1.4.1-compliant than before, not less: the only thing that changed is that the channel which
 * had to be legible **as text** is now the one with contrast to spare.
 *
 * ⛔ **THE 17 PRE-EXISTING BADGE SITES IN THIS PACKAGE (and 57 in `site-admin`) STILL CARRY THE OLD
 * FAILING PATTERN.** `DataBadge` had zero call sites when this was found, so nothing shipped broken —
 * but migrating those 74 changes how every status chip in the product looks, which is a product
 * decision and is escalated, not bundled here.
 *
 * ⚠️ **What this file's own gate does NOT assert, and why:** a composited *border* at `/40` measures
 * 1.48–1.90:1 against its surface, i.e. far below 3:1 — but a badge is identified by its **label and
 * its fill**, so the border is not the sole means of identifying it and 1.4.11 does not bind on it.
 * Asserting 3:1 there would fail correct code across the whole package and get the gate weakened.
 * ⛔ That judgement is not mechanisable and is stated rather than encoded.
 */
import { CircleCheck, CircleX, Info, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * ⭐ **THE CLOSED VOCABULARY.** Five arms, because there are five things a data value can say — and
 * ⛔ the fifth is not a colour, which is the point of including it.
 *
 * · `good`     — the measured value IS the wanted one. healthy · in-sync · ready · live · succeeded.
 * · `caution`  — measured, off-nominal, not failing. degraded · stale · partial · deprecated-still-running.
 * · `bad`      — measured, and it is a failure. unhealthy · unavailable · failed · blocked · error.
 * · `notable`  — ⭐ true, **not a fault**, and must be noticed. drifted · multi-version · upgrade in flight.
 * · `none`     — ⛔ **NO VERDICT.** Identity, chrome, an unenumerated value, and every absence.
 *
 * ## ⛔ WHY `notable` IS NOT FOLDED INTO `caution`, WHICH WAS THE OBVIOUS SIMPLIFICATION
 *
 * A data plane mid-upgrade is **drifted on purpose** — that is a rollout working, not a rollout
 * failing. Colouring it amber puts a healthy in-flight deploy in the same bucket as a degraded one,
 * and an operator triaging by colour goes and looks at it. `AppOverviewPane` had already reached
 * this conclusion locally (`drifted: 'text-state-info'`) and `AppHistoryPane` independently reached
 * it too (`partial: 'text-state-info'`); this arm is those two agreeing, promoted.
 *
 * ## ⛔ AND WHY `none` IS AN ARM RATHER THAN AN ABSENT KEY
 *
 * A map with four keys makes "this value has no verdict" the *default* — i.e. whatever the
 * surrounding `className` happened to be. That is how a status nobody enumerated ends up green: it
 * falls through to the row's tone. Naming it forces the call site to say so out loud, and lets
 * {@link dataToneClass} return a real neutral rather than an empty string.
 */
export const DATA_VERDICTS = ['good', 'caution', 'bad', 'notable', 'none'] as const;
export type DataVerdict = (typeof DATA_VERDICTS)[number];

/**
 * How a verdict is being drawn. ⛔ The alpha vocabulary lives HERE and nowhere else — defect 2
 * above is four files each choosing `/40` vs `/50` vs `/70` vs `/80` for one semantic.
 *
 * · `text`  — a value in a cell or inline. Colour on the glyph and the word.
 * · `badge` — a pill: tinted fill, matching border, coloured label.
 * · `fill`  — a NON-TEXT mark: a dot, a square, a bar segment. ⚠️ Full opacity, and it must clear
 *             WCAG 1.4.11's 3:1 for non-text UI rather than 4.5:1 — both the old and the new
 *             success green do, so this role is unaffected by the token fix. ⛔ Its `none` arm is
 *             **unfilled with a dashed edge**, see the table.
 * · `panel` — a FULL-WIDTH banner or framed block: a lighter tint than a badge, because it covers far
 *             more area and a `/10` fill across a whole panel reads as a coloured box rather than a
 *             tinted note.
 * · `edge`  — a RULE: a hairline accent on ONE side of a block, where a `panel`'s frame-plus-wash is
 *             too loud for the space. ⚠️ Full opacity, for the same reason as `fill` and not as a
 *             style choice: an edge is a **non-text** object, so it answers WCAG 1.4.11's 3:1, and a
 *             1–3px line at `/40` does not clear it. ⛔ The WIDTH and the SIDE are GEOMETRY and belong
 *             to the call site (`border-l-2`), chosen by the surface rather than by the data — this
 *             role supplies the colour and the line STYLE only.
 *
 * ## ⛔ `edge` IS A CHANNEL THE DESIGN LANGUAGE ALREADY SPECIFIES AND THIS TABLE HAD NO ROW FOR
 *
 * `DESIGN-RESET.md` §3.1: *"Every level says itself four ways: **the 3px gutter edge**, a distinct
 * glyph, the word, and the colour"*, and, of `--color-accent`, *"it appears only as a fill or a 2px
 * edge, where the binding floor is 3:1"*. ⇒ a coloured edge is a first-class verdict channel in this
 * system, and until now the only place to get one was to type `border-state-critical` at the call
 * site — which is precisely what `SplitTreeLayout`'s fault rail did, and precisely what the ratchet
 * caught. Same shape as `panel`'s history one row up.
 *
 * ⚠️ The first adopter was `SplitTreeLayout`'s diagnosis rail. Today 16 production files use the role,
 * among them the destructive-button edges of the panes. `NavRail`'s left edges are `accent`/`border-strong`
 * chrome that carries selection, not a verdict, so they do not use it.
 *
 * ## ⛔ `panel` WAS MISSING AND THAT WAS A REAL GAP IN THIS FILE, not a completeness exercise
 *
 * Found by the ratchet, which is the point of having one. `absence.tsx` has used
 * `border-state-warning/40 bg-state-warning/5` for its panel frames since before this module existed
 * — a **fourth alpha** (`/5`) that the first version of this table had no role for. So a panel-scale
 * banner had nowhere sanctioned to come from, and the next one duly hand-wrote the same three classes:
 * `DataPlaneMetricsScreen.tsx:281` reproduces `absence.tsx:209`'s tone **byte-identically**.
 *
 * ⇒ that is the alpha zoo this module exists to end, reappearing in a role I had not enumerated. ⛔ A
 * vocabulary missing a role does not prevent the decision; it just moves it back to the call site.
 */
export const TONE_ROLES = ['text', 'badge', 'fill', 'panel', 'edge'] as const;
export type ToneRole = (typeof TONE_ROLES)[number];

/**
 * ⭐ **THE TABLE.** Every state colour in this package resolves through here.
 *
 * ⛔ `none` is deliberately NOT `''`. An empty string inherits, and inheriting is how an
 * unenumerated status picks up the tone of whatever wraps it — the `A13` defect `TenantsPane`
 * already annotates: *"Neutral is the tone for a status nobody enumerated — never green."*
 */
const TONE: Readonly<Record<ToneRole, Readonly<Record<DataVerdict, string>>>> = {
  text: {
    good: 'text-state-success',
    caution: 'text-state-warning',
    bad: 'text-state-critical',
    notable: 'text-state-info',
    none: 'text-secondary',
  },
  /*
   * ⛔⛔ **THE LABEL IS `text-primary`, NOT THE STATE COLOUR — and that is a MEASURED WCAG FIX, not a
   * style preference.** See the module header's §5.
   */
  badge: {
    good: 'border-state-success/40 bg-state-success/10 text-primary',
    caution: 'border-state-warning/40 bg-state-warning/10 text-primary',
    bad: 'border-state-critical/40 bg-state-critical/10 text-primary',
    notable: 'border-state-info/40 bg-state-info/10 text-primary',
    none: 'border-border-subtle bg-surface-muted text-secondary',
  },
  /* ⛔ Same measured fix as `badge` — a `/5` tint of the token still leaves its own text at 4.17:1
   * (critical, light). The label is `text-primary`; the tint, border and glyph carry the verdict. */
  panel: {
    good: 'border-state-success/40 bg-state-success/5 text-primary',
    caution: 'border-state-warning/40 bg-state-warning/5 text-primary',
    bad: 'border-state-critical/40 bg-state-critical/5 text-primary',
    notable: 'border-state-info/40 bg-state-info/5 text-primary',
    none: 'border-border-subtle bg-surface-muted text-secondary',
  },
  /*
   * ⛔⛔ **`none` IS NOT FILLED AT ALL, AND THAT IS THE ONE CELL IN THIS TABLE THAT CARRIES A
   * NON-COLOUR CHANNEL BY ITSELF.**
   *
   * Every other role distinguishes `none` from `good` twice over: a different class string *and* the
   * absence of a glyph ({@link VERDICT_ICON}). A mark at record scale has no room for a glyph — an 8px
   * icon is a smudge — so if `none` were merely *a different fill colour* it would be separated from
   * `good` by **hue alone**. Grey-vs-green is exactly the pair a deuteranope reads as one colour, and
   * *"we did not measure"* rendering as *"fine"* is the absence-collapse this package spent the week
   * closing (`CellAbsence` vs `InFlightPane`, defect 3 in the header).
   *
   * ⇒ the distinction moves into the SHAPE: unfilled, dashed. A hollow dashed ring and a solid disc
   * differ in a monochrome screenshot, at 8px, and to every form of colour vision. ⚠️ It also reads
   * correctly as a *sentence*: an outline is a slot nothing has been put in yet.
   *
   * ⚠️ This supersedes `AppOverviewPane`'s local `'no-channel': 'bg-border-strong'`, which had the
   * semantics right (grey, deliberately not a fault colour — "nobody told us" and "broken" must not
   * look alike) and the channel wrong. The token is kept, as the edge instead of the fill:
   * `--color-border-strong` measures **4.13 : 4.32 : 4.02** against the three light surfaces and
   * **9.01 : 7.48 : 6.90** dark, so the ring clears WCAG 1.4.11's 3:1 in both polarities with room.
   *
   * ⛔ **NO `bg-*` HERE, deliberately** — a transparent mark is the point, and adding a neutral fill
   * back "for consistency" re-collapses the arm.
   */
  fill: {
    good: 'bg-state-success',
    caution: 'bg-state-warning',
    bad: 'bg-state-critical',
    notable: 'bg-state-info',
    none: 'border border-dashed border-border-strong',
  },
  /*
   * ⛔ **NO `border` AND NO SIDE HERE.** The call site owns which edge and how thick
   * (`border-l-2`, `border-l-[3px]`); this row owns only the colour and the line style. Putting
   * `border-l-2` in the table would make one surface's geometry everybody's.
   *
   * ⛔ **`none` IS DASHED, and that is the same non-hue channel `fill`'s `none` uses** — a solid grey
   * rail and a solid green rail differ by hue alone, which is the *"we did not measure"* reading as
   * *"fine"* collapse. ⚠️ It is `border-subtle` rather than `fill`'s `border-strong`, and the two
   * differ for a stated reason rather than by drift: `fill`'s `none` is an **8px ring** that has to be
   * FINDABLE at record scale, whereas an edge is a full-height rule, and `DESIGN-RESET.md` §3.1 gives
   * `border-subtle` as the token for a rule inside a region. The first adopter, `SplitTreeLayout`
   * (deleted in D4 of console-cleanup-g7a), rendered these exact classes before it took this row.
   */
  edge: {
    good: 'border-state-success',
    caution: 'border-state-warning',
    bad: 'border-state-critical',
    notable: 'border-state-info',
    none: 'border-dashed border-border-subtle',
  },
};

/** The class(es) for one verdict in one role. ⛔ The only sanctioned source of a state colour. */
export function dataToneClass(verdict: DataVerdict, role: ToneRole): string {
  return TONE[role][verdict];
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// ⭐⭐ THE SECOND VOCABULARY — an ACTION's consequence, which is NOT a data verdict
// ══════════════════════════════════════════════════════════════════════════════════════════════

/**
 * ⭐⭐ **HOW LOUD A ROW-SHAPED CONTROL IS — and it is a DIFFERENT VOCABULARY from {@link DataVerdict},
 * sharing only the `state-critical` token.**
 *
 * ## ⛔ WHY THIS IS NOT `dataToneClass(v, role)` WITH A FIFTH ROLE
 *
 * This module's own specification is *"colour encodes a **verdict**, never a **value**. A field may
 * carry colour iff the code can name which verdict its current value carries."* ⛔ A menu row is not
 * a field and has no value. Nothing has been measured, so there is no verdict to name — the row is
 * offered **precisely because nothing has failed yet**. Routing it through {@link dataToneClass}
 * would make the code say *"this item's datum is in a critical state"*, which is false, and would
 * make any future gate reading *"every `data-verdict-tone` names a measurement"* unfalsifiable at
 * exactly the call sites most likely to want to lie.
 *
 * ⭐ **The line is already drawn in this repo, by the ratchet's own exemption list**, which exempts
 * `shell/Button.tsx` on this reasoning: *"whose `critical` variant is a **control** affordance …
 * A destructive button is not reporting a measurement; it is offering an action. Different job,
 * different rules."* An `ActionMenuItem` **is** a `<button>` — its own header says it exists to
 * *"remove the last reason for a pane to hand-roll a `<button>`"*. Same job ⇒ same rule.
 *
 * ## ⛔ WHY IT LIVES HERE RATHER THAN IN `Button.tsx` OR A THIRD MODULE
 *
 * · Not `Button.tsx`: every entry of `BUTTON_TONE_CLASS` carries a `disabled:` clause by contract,
 *   and a menu row is forbidden the `disabled` attribute (permission hides, state uses
 *   `aria-disabled`). Its filled `danger` (`bg-state-critical text-on-accent`) is also the wrong
 *   weight — a full-bleed red row in a dropdown. The two are the same *decision* at two weights;
 *   they are not the same *string*.
 * · Not a third module: the exemption's whole stated reason is *"the classes live in exactly one
 *   file, so that file cannot be counted against itself."* A third exempt file for a two-entry map
 *   dilutes that, and a module with one adopter is the unadopted-primitive shape this package has
 *   already shipped three times.
 *
 * ⇒ this file's header is *"THE ONE PLACE COLOUR IS ASSIGNED A MEANING"*, which is broader than *"the
 * one place a DATA verdict is"*. Two vocabularies, one file, named apart so no call site can confuse
 * them. ⛔ **The gate needs no new exemption for this** — nothing was carved out of the corpus; the
 * decision moved INTO the definition that was already exempt.
 *
 * ## ⚠️ THE SECOND SITE ALREADY EXISTS, WHICH IS WHY THIS IS A VOCABULARY AND NOT A ONE-OFF
 *
 * `UserMenu.tsx:463`'s sign-out row hand-writes `text-state-critical … hover:bg-state-critical/10`
 * **byte-identically** to `ActionMenu`'s `danger`, independently authored. That is defect 1 in this
 * module's header — twelve authors of one decision — reappearing in the action vocabulary. It is
 * outside this change's fence and is left to its owner; the home now exists for it.
 */
export const ACTION_TONES = ['normal', 'danger'] as const;
export type ActionTone = (typeof ACTION_TONES)[number];

/**
 * ⛔ `normal` carries NO state colour, and that is the same argument as {@link DATA_VERDICTS}'s
 * `none`: an action with no consequence worth flagging must say so with a real neutral rather than
 * inherit whatever wraps it. ⚠️ Hover is part of the tone, not chrome — it is the only channel that
 * distinguishes the two on a row that has no border and no fill at rest.
 */
const ACTION_TONE_CLASS: Readonly<Record<ActionTone, string>> = {
  normal: 'text-secondary hover:bg-surface-muted hover:text-primary',
  danger: 'text-state-critical hover:bg-state-critical/10',
};

/** The class(es) for one action tone. ⛔ The only sanctioned source of a destructive-control colour. */
export function actionToneClass(tone: ActionTone): string {
  return ACTION_TONE_CLASS[tone];
}

/**
 * ⛔ **EVERY ABSENCE IS `none`, AND THAT IS A RULING, NOT AN OMISSION.**
 *
 * Defect 3 above: `data-absence="notcollected"` renders neutral in `CellAbsence` and amber in
 * `InFlightPane`. Resolving it needs a reason, and the reason is that **colour follows the SUBJECT
 * of the verdict**:
 *
 * · **In a cell, the subject is the row's entity.** "We could not read this data plane's queue" is
 *   a verdict about our *observability*, not about the data plane. Painting it amber makes an
 *   unmeasured data plane look degraded, so an operator triaging by colour chases the wrong row —
 *   the cousin of the *"did not look = found nothing"* collapse this package already fights, in the
 *   form **"did not look = looks bad."**
 * · **In a panel, the subject is the panel's whole question.** There, *"this cannot be measured"*
 *   IS the answer, so {@link NotCollectedState}'s amber frame stays.
 *
 * ⚠️ This REPLACES the reason `absence.tsx` currently gives for the same choice — it says
 * `text-state-warning` "measures 3.04:1 in light mode", which was true when written and is **stale
 * as of the 2026-08-05 recalibration** (that token is now 4.63:1). A rule resting on a number that
 * has since moved is a rule that gets reverted by the next person who re-measures. The subject
 * argument does not expire.
 *
 * ⛔ The unmeasured fleet still has to be visible — it just gets its visibility at the level where
 * the claim is true: the panel-level *"k of n could not be read"* count `DataPlanesPane` already
 * renders in one live region (`dp-status-unchecked`). One `caution` at the panel, not n `caution`s in the
 * rows.
 *
 * ## ⛔⛔ THE PRECONDITION ON APPLYING THIS — lead's condition, and it is not optional
 *
 * The argument above only holds **if the visibility moves UP a level.** ⇒ before turning a coloured
 * absence neutral on any surface, verify that surface has a panel-level count. **Where it does not,
 * add the count or leave the cell alone** — otherwise the signal has not been relocated, it has been
 * deleted, and the ruling would be right in one place and silently wrong in another.
 *
 * ⭐ **Measured 2026-08-06 — there are exactly THREE coloured-absence sites in this package**, and
 * this workstream touched **none** of them, so nothing has been relocated yet:
 *
 * | site | has a panel-level count? |
 * |---|---|
 * | `InFlightPane.tsx:815` (DLQ depth) | ⛔ **NO** — must gain one first |
 * | `FleetCapacityPane.tsx:500` | ⛔ **NO** — must gain one first |
 * | `FleetCapacityPane.tsx:509` | ⛔ **NO** — must gain one first |
 *
 * ⚠️ All three are outside R-8's scope (DP-detail + Platform surfaces), so they keep their amber for
 * now — which is the correct outcome, not an oversight: **an inconsistency that is measured, listed
 * and explained is safer than a consistency bought by deleting a signal.**
 */
export const DATA_VERDICT_FOR_ABSENCE: DataVerdict = 'none';

/**
 * The glyph per verdict — the **redundant** channel, so colour is never alone (WCAG 1.4.1).
 *
 * ⛔ `none` has NO glyph, and that is consistent rather than a gap: `none` means *not coloured*, so
 * there is no colour for a glyph to be redundant with. An icon on every identity cell would be
 * noise on the columns that most need to stay scannable.
 */
const VERDICT_ICON: Readonly<Record<DataVerdict, typeof CircleCheck | null>> = {
  good: CircleCheck,
  caution: TriangleAlert,
  bad: CircleX,
  notable: Info,
  none: null,
};

/** Whether this verdict draws a glyph. Exported so the gate can assert the pairing. */
export function hasVerdictGlyph(verdict: DataVerdict): boolean {
  return VERDICT_ICON[verdict] !== null;
}

function VerdictGlyph({ verdict }: { readonly verdict: DataVerdict }): React.JSX.Element | null {
  const Icon = VERDICT_ICON[verdict];
  if (Icon === null) return null;
  /* `aria-hidden` because {@link DataValue}'s own children already say the word. Announcing
     "check circle healthy" is noise, not redundancy — the redundancy is for EYES. */
  return <Icon aria-hidden="true" className="h-3 w-3 shrink-0" />;
}

/**
 * ⭐ **A DATA VALUE THAT CARRIES A VERDICT.** The workhorse: one cell, one verdict, three channels.
 *
 * ⛔ `children` must be **the word or the number itself** — `healthy`, `12 of 12 ready`,
 * `4.10.2 (out of sync)`. Never an icon alone and never a bare colour: if the text is removed the
 * cell must still be wrong, not merely uncoloured.
 *
 * ⛔ **No `role="status"`, no `aria-live`.** These render one per row. `absence.tsx` documents the
 * defect at length — n live regions in a repeated template announce the same sentence n times
 * before a screen-reader user reaches the first row.
 */
export function DataValue({
  verdict,
  children,
  testId,
  title,
  reason,
  className = '',
  attrs,
}: {
  readonly verdict: DataVerdict;
  readonly children: ReactNode;
  readonly testId: string;
  /** Hover detail. ⛔ Never the only channel for anything load-bearing — see `children`. */
  readonly title?: string;
  /**
   * ⛔⛔ **THE FULL SENTENCE, REACHABLE BY A SCREEN READER — the channel this component lacked.**
   *
   * `title` is a MOUSE channel and must never be the only one: this package documents that defect on
   * `FleetCapacityPane`'s levers, and `CellAbsence` (`absence.tsx`) already pairs a short visible
   * label with an `sr-only` reason for exactly this. Without this prop a caller with an explanation
   * either ships a title-only cell or hand-rolls a sibling span beside the primitive — which is how
   * "one definition" decays into many.
   *
   * ⚠️ Pass the SAME string to `title` and `reason`: two channels for one sentence, not two
   * sentences. A caller that lets them drift has told two readers different things.
   *
   * ⛔ Not rendered when absent — no empty `sr-only` node, which would be an element an assertion can
   * find that says nothing.
   *
   * ⚠️ **AND IT EXISTS BECAUSE `attrs={{ title: … }}` IS SILENTLY DISCARDED.** `attrs` is spread
   * BEFORE this component's own `title` below, so a caller passing a title through `attrs` has
   * `title={undefined}` win, React omits the attribute, and the sentence reaches the reader through
   * ZERO channels — type-checked, invisible, and untested (the existing rows assert only `data-*`
   * and the tone). The suite carries a positive control for exactly that drop.
   */
  readonly reason?: string;
  /** Type/weight/family only. ⛔ Not colour: that is this component's job. */
  readonly className?: string;
  /**
   * ⭐ **THE DOMAIN'S OWN MACHINE-CHECKABLE FACT, carried onto the same element.**
   *
   * Same shape and same reason as `ScrollTable`'s `rowAttrs` — *"used to carry state into the DOM
   * where jsdom cannot see a colour"*. ⛔ It exists because the domain word and the TONE are two
   * different facts and a screen must be able to assert each without the other: `DataPlanesPane`
   * pins `data-verdict="stale"` in four tests, and the tone cannot reconstruct it — `stale` and any
   * future second caution-level verdict both map to `caution`. Dropping the domain attribute "to
   * avoid duplication" would silently weaken those four assertions to a colour check.
   *
   * ⚠️ Passed BEFORE the component's own attributes below, so a call site cannot overwrite
   * `data-verdict-tone` or `data-testid` — a caller that shadowed the tone would make the gate
   * unfalsifiable at exactly the call sites most likely to want to.
   */
  readonly attrs?: Readonly<Record<string, string>>;
}): React.JSX.Element {
  return (
    <span
      {...attrs}
      data-verdict-tone={verdict}
      data-testid={testId}
      title={title}
      className={`inline-flex items-center gap-1 text-xs ${dataToneClass(verdict, 'text')} ${className}`}
    >
      <VerdictGlyph verdict={verdict} />
      {children}
      {reason !== undefined && <span className="sr-only">{reason}</span>}
    </span>
  );
}

/**
 * The pill form, for a lifecycle/status word that reads as a chip rather than as prose.
 *
 * ⚠️ Prefer {@link DataValue} in a table. A pill in every row of a nine-column table is nine
 * borders competing with the row rules, and `ScrollTable`'s sort carets already live up there.
 */
export function DataBadge({
  verdict,
  children,
  testId,
  title,
}: {
  readonly verdict: DataVerdict;
  readonly children: ReactNode;
  readonly testId: string;
  readonly title?: string;
}): React.JSX.Element {
  return (
    <span
      data-verdict-tone={verdict}
      data-testid={testId}
      title={title}
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-2xs font-medium ${dataToneClass(verdict, 'badge')}`}
    >
      {/* ⛔ The glyph carries its OWN colour, because the container's is now `text-primary`. It is a
          non-text mark, so it answers WCAG 1.4.11's 3:1 rather than 1.4.3's 4.5:1 — and measured on its
          own /10 tint the worst case is 3.87:1 (critical, light), which clears it. See §5. */}
      <span className={dataToneClass(verdict, 'text')}>
        <VerdictGlyph verdict={verdict} />
      </span>
      {children}
    </span>
  );
}
